/**
 * WarehouseDashboard — DEPO: Birleşik Ürün Yönetim Merkezi.
 *
 * Mobil öncelikli, TEK sütun, yatay kaydırma YOK. Her ürün kartı
 * akordeon olarak açılır ve dört hızlı işlem sekmesi sunar:
 *   Bestand (sayım/fire — stok motoru RPC) · Preis · Lagerort ·
 *   Kundenpreise · Shop-Push (fiyat+stok → Shopify).
 *
 * KURALLAR DEĞİŞMEDİ:
 *  - Stok yazımı YALNIZCA adjust_stock RPC (audit + kilit; doğrudan
 *    Product.update ile stok yazmak yasak).
 *  - Müşteri fiyatları upsertCustomerPrice (tek yazma noktası).
 *  - Yeni ürün / tam düzenleme ProductEditor (tek editör, stok-güvenli).
 *  - Shop-Push: admin-only proxy, yalnızca fiyat+stok mutation'ları.
 */
import React, { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Warehouse, Search, Package, Plus, Pencil, Loader2, MapPin, Euro,
  ClipboardCheck, Users, Store, ChevronDown, AlertTriangle, CheckCircle2,
  Boxes, Send, History as HistoryIcon,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import ProductEditor from '@/components/products/ProductEditor';
import ProductRequestModal from '@/components/modals/ProductRequestModal';
import { adjustStock, StockApiError, stockErrorToGerman } from '@/lib/stockApi';
import { upsertCustomerPrice } from '@/lib/customerPriceApi';
import { safeEntityUpdate } from '@/lib/safeUpdate';
import { thumb, IMG_PROPS } from '@/lib/imageUrl';
import { pushProductToShop } from '@/lib/shopifyPush';
import { useAuth } from '@/api/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const parseVariants = (v) => {
  if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; } }
  return Array.isArray(v) ? v : [];
};

/* ══════════════════ Bestand (sayım + fire) ══════════════════ */
function BestandSection({ product, user, onDone }) {
  const variants = parseVariants(product.variants);
  const [variantValue, setVariantValue] = useState(variants[0]?.value || '');
  const [mode, setMode] = useState('zaehlung'); // zaehlung | bruch | muster | eigenbedarf
  const [amount, setAmount] = useState('');
  const [pending, setPending] = useState(false);

  const currentStock = variants.length > 0
    ? (variants.find((v) => v.value === variantValue)?.stock ?? 0)
    : (product.stock ?? 0);

  const submit = async () => {
    const num = parseInt(amount);
    if (!Number.isFinite(num) || num < 0) { toast.error('Bitte gültige Menge eingeben.'); return; }

    // Zählung = mutlak değer → delta; fire türleri = negatif delta
    const delta = mode === 'zaehlung' ? num - currentStock : -num;
    if (delta === 0) { toast.info('Keine Änderung — Bestand ist bereits ' + num + '.'); return; }
    if (mode !== 'zaehlung' && num > currentStock) {
      toast.error('Nicht genügend Bestand.');
      return;
    }

    setPending(true);
    try {
      await adjustStock({
        productId: product.id,
        variantValue: variants.length > 0 ? variantValue : null,
        delta,
        type: mode === 'zaehlung' ? 'inventur' : mode,
        reason: mode === 'zaehlung' ? `Zählung: ${currentStock} → ${num}` : mode,
        actorEmail: user?.email,
        sourceTable: 'warehouse_dashboard',
      });
      toast.success(mode === 'zaehlung'
        ? `Bestand korrigiert: ${currentStock} → ${num} (Bewegung protokolliert).`
        : `${num} Stk als ${mode} gebucht.`);
      setAmount('');
      onDone();
    } catch (err) {
      toast.error(err instanceof StockApiError ? stockErrorToGerman(err) : err.message);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="space-y-2">
      {variants.length > 0 && (
        <Select value={variantValue} onValueChange={setVariantValue}>
          <SelectTrigger className="h-11 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {variants.map((v) => (
              <SelectItem key={v.value} value={v.value} className="text-xs">
                {v.value} · Bestand {v.stock ?? 0}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <div className="grid grid-cols-2 gap-1.5">
        {[
          { key: 'zaehlung', label: 'Zählung (Ist)' },
          { key: 'bruch', label: 'Bruch' },
          { key: 'muster', label: 'Muster' },
          { key: 'eigenbedarf', label: 'Eigenbedarf' },
        ].map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setMode(m.key)}
            className={cn(
              'min-h-[40px] rounded-lg border text-xs font-semibold transition-colors cursor-pointer',
              mode === m.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'
            )}
          >
            {m.label}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          type="number" inputMode="numeric" min="0"
          placeholder={mode === 'zaehlung' ? `Gezählter Bestand (aktuell ${currentStock})` : 'Menge'}
          value={amount} onChange={(e) => setAmount(e.target.value)}
          className="h-11 text-sm"
        />
        <Button className="h-11 bg-slate-900 hover:bg-slate-800 text-xs font-bold px-5"
          disabled={pending || amount === ''} onClick={submit}>
          {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buchen'}
        </Button>
      </div>
      <p className="text-[10px] text-slate-400">
        Jede Änderung läuft über die Bestands-Engine (Audit-Protokoll, Bewegungen sichtbar unter „Bewegungen").
      </p>
    </div>
  );
}

/* ══════════════════ Preis + Lagerort (metadata) ══════════════════ */
function StammSection({ product, onDone }) {
  // PIM: Preis wird in Produkte (Produktkarte) gepflegt — hier nur Lagerort.
  const [lagerort, setLagerort] = useState(product.lagerort || '');
  const [pending, setPending] = useState(false);

  const submit = async () => {
    setPending(true);
    try {
      const { skipped } = await safeEntityUpdate(
        base44.entities.Product, product.id, { lagerort }
      );
      toast.success('Lagerort gespeichert.');
      if (skipped.length > 0) {
        toast.warning(`Übersprungen (DB-Spalte fehlt): ${skipped.join(', ')}`);
      }
      onDone();
    } catch (err) {
      toast.error('Fehler: ' + err.message);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-2">
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Lagerort
          </label>
          <Input value={lagerort} placeholder="z.B. A-12, Regal 4"
            onChange={(e) => setLagerort(e.target.value)} className="h-11 text-sm font-mono" />
        </div>
      </div>
      <Button className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-xs font-bold"
        disabled={pending} onClick={submit}>
        {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}
      </Button>
    </div>
  );
}

/* ══════════════════ Kundenpreise (ürün bazlı) ══════════════════ */
function KundenpreiseSection({ product }) {
  const queryClient = useQueryClient();
  const [addLeadId, setAddLeadId] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [editing, setEditing] = useState(null); // cp.id

  const { data: prices = [], isLoading } = useQuery({
    queryKey: ['product-customer-prices', product.id],
    queryFn: () => base44.entities.CustomerPrice.filter({ product_id: product.id }),
  });
  const { data: leads = [] } = useQuery({
    queryKey: ['leads'],
    queryFn: () => base44.entities.Lead.list(),
  });

  const save = async (leadId, leadName) => {
    const parsed = parseFloat(priceInput);
    if (!Number.isFinite(parsed) || parsed < 0) { toast.error('Gültigen Preis eingeben.'); return; }
    try {
      await upsertCustomerPrice({
        leadId, leadName,
        productId: product.id,
        productName: product.name,
        productSku: product.sku || '',
        price: parsed,
        originalPrice: product.price ?? 0,
      });
      queryClient.invalidateQueries(['product-customer-prices', product.id]);
      queryClient.invalidateQueries(['customer-prices']);
      toast.success('Kundenpreis gespeichert.');
      setEditing(null); setAddLeadId(''); setPriceInput('');
    } catch (err) {
      toast.error('Fehler: ' + err.message);
    }
  };

  return (
    <div className="space-y-2">
      {isLoading && <p className="text-xs text-slate-400 py-2">Lade Kundenpreise…</p>}
      {prices.map((cp) => (
        <div key={cp.id} className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
          <p className="text-xs font-semibold text-slate-800 truncate">{cp.lead_name || cp.lead_id}</p>
          {editing === cp.id ? (
            <div className="flex items-center gap-1.5">
              <Input type="number" inputMode="decimal" step="0.01" value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)} className="h-9 w-24 text-right text-xs" autoFocus />
              <Button size="sm" className="h-9 text-[10px] bg-slate-900" onClick={() => save(cp.lead_id, cp.lead_name)}>OK</Button>
            </div>
          ) : (
            <button
              type="button"
              className="text-xs font-mono font-bold text-blue-700 cursor-pointer hover:underline min-h-[36px]"
              onClick={() => { setEditing(cp.id); setPriceInput(String(cp.custom_price ?? cp.preis ?? '')); }}
            >
              €{Number(cp.custom_price ?? cp.preis ?? 0).toFixed(2)} <Pencil className="w-3 h-3 inline ml-0.5" />
            </button>
          )}
        </div>
      ))}
      {!isLoading && prices.length === 0 && (
        <p className="text-xs text-slate-400 py-1">Noch keine Kundenpreise für dieses Produkt.</p>
      )}

      {/* Yeni müşteri fiyatı */}
      <div className="flex gap-1.5 pt-1 border-t border-slate-100">
        <Select value={addLeadId} onValueChange={setAddLeadId}>
          <SelectTrigger className="h-11 flex-1 text-xs"><SelectValue placeholder="Kunde wählen…" /></SelectTrigger>
          <SelectContent>
            {leads.map((l) => (
              <SelectItem key={l.id} value={String(l.id)} className="text-xs">{l.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input type="number" inputMode="decimal" step="0.01" placeholder="€"
          value={editing === null ? priceInput : ''} onChange={(e) => { setEditing(null); setPriceInput(e.target.value); }}
          className="h-11 w-24 text-right text-xs" />
        <Button className="h-11 text-[10px] font-bold bg-slate-900" disabled={!addLeadId}
          onClick={() => {
            const lead = leads.find((l) => String(l.id) === addLeadId);
            if (lead) save(lead.id, lead.name);
          }}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/* ══════════════════ Shop-Push ══════════════════ */
function ShopSection({ product }) {
  const [pending, setPending] = useState(false);
  const [results, setResults] = useState(null);

  const push = async () => {
    setPending(true); setResults(null);
    try {
      const res = await pushProductToShop(product);
      setResults(res);
      const ok = res.filter((r) => r.ok).length;
      if (ok > 0) toast.success(`${ok} SKU an den Shop übertragen (Preis + Bestand).`);
      res.filter((r) => !r.ok).forEach((r) => toast.warning(`${r.sku}: ${r.message}`));
    } catch (err) {
      toast.error('Shop-Push fehlgeschlagen: ' + err.message);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-slate-500">
        Überträgt <b>Preis (€{Number(product.price || 0).toFixed(2)})</b> und
        <b> Bestand ({product.stock ?? 0})</b> per SKU an den Online-Shop.
      </p>
      <Button className="w-full h-11 bg-emerald-700 hover:bg-emerald-800 text-xs font-bold gap-2"
        disabled={pending} onClick={push}>
        {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        An Shop senden
      </Button>
      {results && (
        <div className="space-y-1">
          {results.map((r, i) => (
            <p key={i} className={cn('text-[10px] flex items-center gap-1', r.ok ? 'text-emerald-700' : 'text-red-600')}>
              {r.ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
              {r.sku}: {r.message}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════ Bewegungen (stok tarihçesi) ══════════════════ */
// Etiketler AdminStockMovements ile aynı sözlük — kullanıcı iki ekranda
// aynı dili görür. Kaynak: stock_movements audit tablosu (RPC'ler yazar).
const MOVEMENT_LABELS = {
  reserve:      { label: 'Reserviert (Auftrag)', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  release:      { label: 'Freigegeben',          color: 'bg-slate-100 text-slate-600 border-slate-200' },
  commit:       { label: 'Verkauf/Ausgang',      color: 'bg-red-50 text-red-700 border-red-200' },
  inventur:     { label: 'Inventur/Zählung',     color: 'bg-purple-50 text-purple-700 border-purple-200' },
  wareneingang: { label: 'Wareneingang',         color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  bruch:        { label: 'Bruch',                color: 'bg-orange-50 text-orange-700 border-orange-200' },
  muster:       { label: 'Muster',               color: 'bg-amber-50 text-amber-700 border-amber-200' },
  eigenbedarf:  { label: 'Eigenbedarf',          color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  conversion:   { label: 'Umwandlung',           color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  adjustment:   { label: 'Korrektur',            color: 'bg-slate-100 text-slate-600 border-slate-200' },
  backorder:    { label: 'Backorder',            color: 'bg-red-100 text-red-800 border-red-300' },
};

// Sipariş kaynağı → kanal etiketi (müşteri satırının yanında görünür)
const CHANNEL_LABELS = {
  shopify: 'Online/Shopify',
  ebay: 'eBay',
  website: 'Online',
  agent: 'Agent',
};

const PAGE_SIZE = 7;

// Tarih filtresi ön ayarları — grafik kovası dönem uzunluğuna göre seçilir
// (30T: günlük, 3M: haftalık, 12M: aylık — chart-type kuralı: zaman serisi
// satış adedi → çubuk grafik).
const RANGE_PRESETS = [
  { key: 30, label: '30 T', bucket: 'day' },
  { key: 90, label: '3 M', bucket: 'week' },
  { key: 365, label: '12 M', bucket: 'month' },
];

// Satış grafiği: TÜM siparişlerin kalemleri (Bestellung — teklif ve
// iptal hariç, durumdan bağımsız). Sipariş girildiği an grafiğe düşer.
function buildSalesChart(orders, productId, rangeDays, bucket) {
  const since = Date.now() - rangeDays * 86400000;

  const bucketKey = (d) => {
    if (bucket === 'day') return d.toISOString().slice(0, 10);
    if (bucket === 'week') {
      const monday = new Date(d);
      monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      return monday.toISOString().slice(0, 10);
    }
    return d.toISOString().slice(0, 7); // month
  };
  const bucketLabel = (key) => {
    if (bucket === 'month') {
      const [y, m] = key.split('-');
      return `${m}/${y.slice(2)}`;
    }
    const d = new Date(key);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  const sums = new Map();
  orders.forEach((o) => {
    if (o.status === 'Storniert') return;
    if ((o.order_type || '').toLowerCase() === 'angebot') return;
    const date = new Date(o.created_date || o.created_at);
    if (!date || date.getTime() < since) return;
    let items = o.positionen || o.items || [];
    if (typeof items === 'string') { try { items = JSON.parse(items); } catch { items = []; } }
    (Array.isArray(items) ? items : []).forEach((it) => {
      if (it.product_id !== productId) return;
      const key = bucketKey(date);
      sums.set(key, (sums.get(key) || 0) + (Number(it.quantity) || 0));
    });
  });

  return [...sums.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, qty]) => ({ label: bucketLabel(key), qty }));
}

function BewegungenSection({ product }) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [rangeDays, setRangeDays] = useState(90);

  const { data: allMovements = [], isLoading } = useQuery({
    queryKey: ['stock-movements', product.id],
    queryFn: async () => {
      const rows = await base44.entities.StockMovement.filter({ product_id: product.id });
      return rows.sort((a, b) => new Date(b.created_at || b.created_date) - new Date(a.created_at || a.created_date));
    },
  });

  // Tarih filtresi hem grafiği hem hareket listesini kapsar
  const movements = useMemo(() => {
    const since = Date.now() - rangeDays * 86400000;
    return allMovements.filter((m) => new Date(m.created_at || m.created_date).getTime() >= since);
  }, [allMovements, rangeDays]);

  // Sipariş verisi: hem müşteri adı/kanal eşlemesi hem satış grafiği için.
  // Uygulama genelinde zaten cache'li (queryKey ['orders']).
  const { data: orders = [] } = useQuery({
    queryKey: ['orders'],
    queryFn: () => base44.entities.Order.list(),
  });
  const orderById = useMemo(() => {
    const m = new Map();
    orders.forEach((o) => m.set(String(o.id), o));
    return m;
  }, [orders]);

  const preset = RANGE_PRESETS.find((p) => p.key === rangeDays) || RANGE_PRESETS[1];
  const chartData = useMemo(
    () => buildSalesChart(orders, product.id, rangeDays, preset.bucket),
    [orders, product.id, rangeDays, preset.bucket]
  );
  const totalSold = chartData.reduce((s, d) => s + d.qty, 0);

  const visible = movements.slice(0, visibleCount);
  const remaining = movements.length - visibleCount;

  return (
    <div className="space-y-2">
      {/* Tarih filtresi — grafik + liste birlikte filtrelenir */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1" role="tablist" aria-label="Zeitraum">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={rangeDays === p.key}
              onClick={() => { setRangeDays(p.key); setVisibleCount(PAGE_SIZE); }}
              className={cn(
                'min-h-[36px] px-3 rounded-full text-[11px] font-bold border transition-colors cursor-pointer',
                rangeDays === p.key
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-slate-500 flex-shrink-0">
          Verkauft: <b className="text-slate-900">{totalSold} Stk</b>
        </p>
      </div>

      {/* Satış grafiği (abgeschlossene Verkäufe) — kompakt, sabit yükseklik.
          Satış yoksa büyük boş alan yerine ince bir bilgi satırı. */}
      {chartData.length > 0 ? (
        <div
          className="h-20 bg-white border border-slate-200 rounded-lg px-1 py-1.5"
          role="img"
          aria-label={`Verkaufsdiagramm: ${totalSold} Stück in ${preset.label}`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 2, right: 4, bottom: 0, left: -30 }} barCategoryGap="25%">
              <XAxis dataKey="label" tick={{ fontSize: 8 }} stroke="#94a3b8" interval="preserveStartEnd" tickLine={false} />
              <YAxis tick={{ fontSize: 8 }} stroke="#cbd5e1" allowDecimals={false} width={34} tickLine={false} axisLine={false} />
              <Tooltip
                formatter={(value) => [`${value} Stk`, 'Verkauft']}
                labelStyle={{ fontSize: 11 }}
                contentStyle={{ fontSize: 11, borderRadius: 8, padding: '4px 8px' }}
              />
              <Bar dataKey="qty" fill="#0f172a" radius={[3, 3, 0, 0]} maxBarSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-[10px] text-slate-400 bg-white border border-slate-200 rounded-lg px-3 py-2">
          Keine Bestellungen im Zeitraum.
        </p>
      )}

      {isLoading && <p className="text-xs text-slate-400 py-2">Lade Bewegungen…</p>}
      {!isLoading && movements.length === 0 && (
        <p className="text-xs text-slate-400 py-2">
          Keine Bewegungen im gewählten Zeitraum.
        </p>
      )}
      {visible.map((m) => {
        const style = MOVEMENT_LABELS[m.type] || { label: m.type, color: 'bg-slate-100 text-slate-600 border-slate-200' };
        const when = new Date(m.created_at || m.created_date);
        const positive = Number(m.delta) > 0;

        // Müşteri + kanal (yalnızca sipariş kaynaklı hareketlerde)
        const order = m.source_table === 'orders' && m.source_id
          ? orderById.get(String(m.source_id))
          : null;
        const channel = order
          ? (CHANNEL_LABELS[(order.source || 'agent').toLowerCase()] || order.source)
          : null;

        return (
          <div key={m.id} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2.5 py-2">
            <span className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded border flex-shrink-0', style.color)}>
              {style.label}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-slate-500 truncate">
                {when.toLocaleDateString('de-DE')} {when.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                {m.variant_value ? ` · ${m.variant_value}` : ''}
                {!order && m.reason ? ` · ${m.reason}` : ''}
              </p>
              {order ? (
                <p className="text-[10px] font-semibold text-slate-700 truncate">
                  {order.lead_name || 'Unbekannter Kunde'}
                  <span className="text-slate-400 font-normal"> · {channel}</span>
                </p>
              ) : m.actor_email && (
                <p className="text-[9px] text-slate-400 truncate">{m.actor_email}</p>
              )}
            </div>
            <span className={cn(
              'font-mono text-xs font-black flex-shrink-0',
              positive ? 'text-emerald-700' : 'text-red-600'
            )}>
              {positive ? '+' : ''}{m.delta}
            </span>
          </div>
        );
      })}
      {remaining > 0 && (
        <button
          type="button"
          onClick={() => setVisibleCount(visibleCount + PAGE_SIZE)}
          className="w-full min-h-[44px] text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
        >
          Weitere anzeigen ({remaining} verbleibend)
        </button>
      )}
      <p className="text-[10px] text-slate-400">
        Vollständiges Protokoll (alle Produkte): Admin → Bewegungen. Diese Daten fließen später in die Buchhaltung ein.
      </p>
    </div>
  );
}

/* ══════════════════ Ürün kartı ══════════════════ */
// PIM-Rollenteilung: Depo = physische Ware (Bestand/Lagerort/Historie).
// Preise, Kundenpreise und Shop-Push leben in Produkte (Produktkarte).
const SECTIONS = [
  { key: 'bestand', label: 'Bestand', icon: ClipboardCheck },
  { key: 'stamm', label: 'Lagerort', icon: MapPin },
  { key: 'historie', label: 'Historie', icon: HistoryIcon },
];

function ProductCard({ product, user, onEdit, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState('bestand');
  const variants = parseVariants(product.variants);
  const available = product.available_stock ?? ((product.stock ?? 0) - (product.reserved_stock ?? 0));
  const low = available <= (product.min_stock ?? 0);

  return (
    <Card className={cn('overflow-hidden', product.is_active === false && 'opacity-70 border-dashed')}>
      {/* Özet satırı — 44px+, tüm bilgiler tek sütunda */}
      <button
        type="button"
        className="w-full flex items-center gap-3 p-3 text-left cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <div className="w-11 h-11 rounded-lg border bg-white flex-shrink-0 overflow-hidden flex items-center justify-center">
          {product.image_url
            ? <img {...IMG_PROPS} loading="lazy" width={44} height={44} src={thumb(product.image_url, 88)} alt="" className="w-full h-full object-contain" />
            : <Package className="w-5 h-5 text-slate-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900 truncate">{product.name}</p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
            <span className="text-[10px] font-mono text-slate-400">{product.sku || 'KEINE SKU'}</span>
            {product.lagerort && (
              <span className="text-[10px] font-mono text-sky-700 bg-sky-50 px-1.5 rounded flex items-center gap-0.5">
                <MapPin className="w-2.5 h-2.5" />{product.lagerort}
              </span>
            )}
            {variants.length > 0 && (
              <span className="text-[10px] text-blue-600">{variants.length} Var.</span>
            )}
            {product.is_active === false && (
              <Badge variant="outline" className="text-[9px] h-4 px-1">Inaktiv</Badge>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          {/* Düşük stok: renk + ikon (renk tek gösterge olmasın — a11y) */}
          <p className={cn('text-sm font-black flex items-center justify-end gap-1', low ? 'text-red-600' : 'text-slate-900')}>
            {low && <AlertTriangle className="w-3.5 h-3.5" aria-label="Bestand kritisch" />}
            {available}
          </p>
          <p className="text-[10px] text-slate-400">€{Number(product.price || 0).toFixed(2)}</p>
        </div>
        <ChevronDown className={cn('w-4 h-4 text-slate-400 flex-shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {/* Genişletilmiş panel — sekmeli hızlı işlemler */}
      {open && (
        <div className="border-t bg-slate-50/60 p-3 space-y-3">
          {/* Stok özeti */}
          <div className="grid grid-cols-3 gap-1.5 text-center">
            {[
              { label: 'Bestand', value: product.stock ?? 0 },
              { label: 'Reserviert', value: product.reserved_stock ?? 0 },
              { label: 'Verfügbar', value: available, hot: low },
            ].map((s) => (
              <div key={s.label} className="bg-white border border-slate-200 rounded-lg py-1.5">
                <p className={cn('text-sm font-black', s.hot ? 'text-red-600' : 'text-slate-900')}>{s.value}</p>
                <p className="text-[9px] uppercase text-slate-400 font-bold">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Bölüm sekmeleri — kaydırmasız 5'li grid */}
          <div className="grid grid-cols-5 gap-1">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSection(s.key)}
                className={cn(
                  'min-h-[44px] rounded-lg border flex flex-col items-center justify-center gap-0.5 transition-colors cursor-pointer',
                  section === s.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200'
                )}
              >
                <s.icon className="w-3.5 h-3.5" />
                <span className="text-[9px] font-bold">{s.label}</span>
              </button>
            ))}
          </div>

          {section === 'bestand' && <BestandSection product={product} user={user} onDone={onRefresh} />}
          {section === 'stamm' && <StammSection product={product} onDone={onRefresh} />}
          {section === 'historie' && <BewegungenSection product={product} />}

          <Button variant="outline" size="sm" className="w-full h-10 text-[10px] font-bold gap-1.5"
            onClick={() => onEdit(product)}>
            <Pencil className="w-3.5 h-3.5" /> Vollständig bearbeiten (Varianten, Lieferanten, Details)
          </Button>
        </div>
      )}
    </Card>
  );
}

/* ══════════════════ Ana sayfa ══════════════════ */
export default function WarehouseDashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('active'); // active | low | inactive
  const [editProduct, setEditProduct] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => base44.entities.Product.list(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const refresh = () => queryClient.invalidateQueries(['products']);

  const filtered = useMemo(() => {
    const t = searchTerm.toLowerCase();
    return products
      .filter((p) => {
        if (activeFilter === 'active') return p.is_active !== false;
        if (activeFilter === 'inactive') return p.is_active === false;
        if (activeFilter === 'low') {
          const avail = p.available_stock ?? ((p.stock ?? 0) - (p.reserved_stock ?? 0));
          return p.is_active !== false && avail <= (p.min_stock ?? 0);
        }
        return true;
      })
      .filter((p) =>
        !t ||
        p.name?.toLowerCase().includes(t) ||
        p.sku?.toLowerCase().includes(t) ||
        p.lagerort?.toLowerCase().includes(t)
      )
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [products, searchTerm, activeFilter]);

  const counts = useMemo(() => ({
    active: products.filter((p) => p.is_active !== false).length,
    low: products.filter((p) => {
      const avail = p.available_stock ?? ((p.stock ?? 0) - (p.reserved_stock ?? 0));
      return p.is_active !== false && avail <= (p.min_stock ?? 0);
    }).length,
    inactive: products.filter((p) => p.is_active === false).length,
  }), [products]);

  return (
    <PageShell
      head={(
          <div className="flex items-center justify-between gap-2">
            <PageHeader icon={<Warehouse className="w-5 h-5" />} title="Depo & Produkte" />
            <div className="flex gap-1.5 flex-shrink-0">
              <Button variant="outline" size="sm" className="h-11 text-[10px] font-bold"
                onClick={() => setShowRequestModal(true)}>
                Anfrage
              </Button>
              <Button size="sm" className="h-11 bg-slate-900 hover:bg-slate-800 text-[10px] font-bold gap-1"
                onClick={() => { setEditProduct(null); setShowEditModal(true); }}>
                <Plus className="w-4 h-4" /> Produkt
              </Button>
            </div>
          </div>
      )}
    >

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Name, SKU oder Lagerort…"
          className="pl-10 h-11 bg-white"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Filtre çipleri — kaydırmasız 3'lü grid */}
      <div className="grid grid-cols-3 gap-1.5">
        {[
          { key: 'active', label: 'Aktiv', count: counts.active, icon: Boxes },
          { key: 'low', label: 'Kritisch', count: counts.low, icon: AlertTriangle },
          { key: 'inactive', label: 'Inaktiv', count: counts.inactive, icon: Package },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setActiveFilter(f.key)}
            className={cn(
              'min-h-[48px] rounded-xl border flex items-center justify-center gap-1.5 transition-colors cursor-pointer',
              activeFilter === f.key
                ? (f.key === 'low' ? 'bg-red-600 text-white border-red-600' : 'bg-slate-900 text-white border-slate-900')
                : 'bg-white text-slate-600 border-slate-200'
            )}
          >
            <f.icon className="w-3.5 h-3.5" />
            <span className="text-xs font-bold">{f.label}</span>
            <span className={cn(
              'text-[10px] font-black px-1.5 rounded-full',
              activeFilter === f.key ? 'bg-white/20' : 'bg-slate-100'
            )}>{f.count}</span>
          </button>
        ))}
      </div>

      {/* Ürün listesi */}
      <div className="space-y-2">
        {isLoading && (
          <p className="text-center text-sm text-slate-400 py-10">
            <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" /> Lade Produkte…
          </p>
        )}
        {!isLoading && filtered.length === 0 && (
          <Card className="border-2 border-dashed p-10 text-center text-slate-400 text-sm">
            Kein Produkt gefunden.
          </Card>
        )}
        {filtered.map((p) => (
          <ProductCard
            key={p.id}
            product={p}
            user={user}
            onRefresh={refresh}
            onEdit={(prod) => { setEditProduct(prod); setShowEditModal(true); }}
          />
        ))}
      </div>

      {/* Modallar */}
      <ProductEditor
        product={editProduct}
        open={showEditModal}
        onOpenChange={(v) => { setShowEditModal(v); if (!v) setEditProduct(null); }}
      />
      <ProductRequestModal
        open={showRequestModal}
        onOpenChange={setShowRequestModal}
      />
    </PageShell>
  );
}
